import type { TUser } from 'forstok-ui-lib';

import cookies from './cookies';

export type LogEvent = {
  source: 'apollo_proxy' | 'apollo_subscription';
  log_type: 'GRAPHQL_ERROR' | 'NETWORK_ERROR' | 'WS_EVENT';
  event_name?: string;
  operation_name?: string;
  operation_type?: 'query' | 'mutation' | 'subscription';
  error_code?: string;
  error_message?: string;
  status_code?: number;
  variables?: unknown;
  metadata?: Record<string, unknown>;
};

export function createFrontendLogger(endpoint: string, readProfileId: () => string | number | null | undefined) {
  const queue: { body: string; retries: number }[] = [];
  let sending = false;
  const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

  async function flush() {
    if (sending) return;
    sending = true;

    try {
      while (queue.length) {
        // Keep queued logs while offline without consuming retry attempts.
        if (!navigator.onLine) {
          await wait(5000);
          continue;
        }

        const entry = queue[0];
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 5000);
        let retry = false;

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'omit',
            signal: controller.signal,
            body: entry.body,
          });
          retry = response.status === 503;
        } catch {
          retry = true;
        } finally {
          window.clearTimeout(timer);
        }

        if (retry && !navigator.onLine) {
          await wait(5000);
          continue;
        }
        if (retry && entry.retries < 3) {
          entry.retries += 1;
          await wait(5000 * entry.retries);
        } else {
          queue.shift();
          await wait(500); // One POST at a time, at most two per second.
        }
      }
    } finally {
      sending = false;
    }
  }

  return (event: LogEvent): void => {
    if (!endpoint || queue.length >= 100) return;

    try {
      const profileId = readProfileId();
      if (profileId == null || String(profileId).trim() === '') return;

      // Snapshot the original profile, time, and variables for delayed delivery.
      const body = JSON.stringify({
        ...event,
        profile_id: String(profileId),
        occurred_at: new Date().toISOString(),
      });
      queue.push({ body, retries: 0 });
      void flush();
    } catch {
      // Drop unserializable logs; never log failures of the logger itself.
    }
  };
}

export const reportFeLog = createFrontendLogger(import.meta.env.VITE_FE_LOG_URL as string, () => {
  const users = cookies.get('users') as TUser | undefined;
  return users?.profile_id;
});
